import Axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';

import { readActiveContextFromStorage, headerForActive } from '../../modules/auth/contexts/ActiveOrgContext';
import { readImpersonationFromStorage } from '../../modules/auth/contexts/ImpersonationContext';

import { ANONYMOUS_REQUEST, unauthorizedMessage, withApiErrorMessage, type RequestAuthState } from './authFailure';

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '';

let accessTokenGetter: (() => Promise<string>) | null = null;

// Null is accepted so a sign-out can put the client back to anonymous rather
// than leaving a getter behind that will only ever throw.
export const setAccessTokenGetter = (getter: (() => Promise<string>) | null) => {
  accessTokenGetter = getter;
};

const AUTH_STATE_KEY = '__wzAuthState';

type TaggedConfig = InternalAxiosRequestConfig & { [AUTH_STATE_KEY]?: RequestAuthState };

/**
 * How this request was authenticated, read back on the response so a 401 can
 * be told apart three ways: a visitor who never had a token, a user whose
 * token was refused, and a user whose token we failed to mint.
 */
function readAuthState(config: unknown): RequestAuthState {
  return (config as TaggedConfig | undefined)?.[AUTH_STATE_KEY] ?? ANONYMOUS_REQUEST;
}

const API = Axios.create({
  baseURL: API_ENDPOINT,
  timeout: 120000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

API.interceptors.request.use(async (config: TaggedConfig) => {
  const auth: RequestAuthState = { tokenAttached: false, tokenFetchFailed: false };
  if (accessTokenGetter) {
    try {
      const token = await accessTokenGetter();
      config.headers.Authorization = `Bearer ${token}`;
      auth.tokenAttached = true;
    } catch {
      // Still sent: the public endpoints on this instance answer anonymously.
      auth.tokenFetchFailed = true;
    }
  }
  config[AUTH_STATE_KEY] = auth;
  // Read the active context from localStorage (not a hook) so the interceptor
  // isn't coupled to React render cycles.
  const header = headerForActive(readActiveContextFromStorage());
  if (header) config.headers['X-Active-Org-Id'] = header;
  // Same pattern for impersonation: the backend swaps Current.user when a
  // system admin sends this header. Sending it when the caller isn't an admin
  // is harmless — the backend ignores it.
  const impersonate = readImpersonationFromStorage();
  if (impersonate) config.headers['X-Impersonate-User-Id'] = String(impersonate.userId);
  return config;
});

/**
 * Classifies auth failures; never navigates. authFailure.ts holds the
 * reasoning: a redirect from here is wrong both for the anonymous visitors
 * this instance also serves and for signed-in users Auth0's own SDK is
 * already redirecting.
 *
 * Sending a request with no Authorization header because the token fetch
 * threw is why a 401 could arrive meaning "we never proved who you are" and
 * read as "your identity was rejected". The request interceptor records which
 * happened; this turns it into a sentence.
 */
API.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (Axios.isAxiosError(error) && error.response) {
      const message = unauthorizedMessage(error.response.status, readAuthState(error.config));
      if (message) error.response.data = withApiErrorMessage(error.response.data, message);
    }
    return Promise.reject(error);
  },
);

export default API;
